import { useCallback, useMemo, useRef, useState } from 'react';
import type { WorkoutLayer } from '@strength/db-schema';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { useToast } from '../../../hooks/useToast';
import { trpc } from '../../../../trpc/react';
import { environment } from '../../../../environment';
import { Org } from '../exercise-library-management/types';
import {
  applySuggestedMappings,
  hasUnresolvedMissingMetrics,
  removeExerciseFromLayers,
  removeMetricFromLayers,
  stripUnresolvedMetricRefs,
  substituteExerciseId,
} from './utils';

// The resolved-template output shape returned by the workout-import stages
// 1-3 pipeline. Kept local so the UI does not import server-only pipeline
// types directly.
export interface WorkoutImportValidationResult {
  name: string;
  template: WorkoutLayer[];
  missingExerciseMapping: Record<
    string,
    { name: string; libraryExerciseId: string }
  >;
  suggestedExerciseMapping: Record<
    string,
    {
      name: string;
      libraryExerciseId: string;
      suggestedName: string;
      suggestedId: string;
    }
  >;
  missingMetricMapping: Record<
    string,
    { name: string; type: string; metricId: string }
  >;
  suggestedMetricMapping: Record<
    string,
    {
      name: string;
      type: string;
      metricId: string;
      suggestedName: string;
      suggestedId: string;
    }
  >;
}

// Read a File and return its contents as a base64 string (no data-URL prefix).
// Covers both CSV text and PDF binary — the backend (via the import MCP) decodes
// + parses server-side, so the client does no parsing.
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // readAsDataURL returns "data:<mime>;base64,<payload>" — strip the prefix.
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file as base64'));
        return;
      }
      const commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('File read error'));
    reader.readAsDataURL(file);
  });
}

// The stage-3 resolved template shape returned by
// /internal/workout-import-agent. Mirrors ResolvedTemplate from
// @strength/workout-import but kept local so the web app doesn't import
// server-only pipeline types. The matched* mappings are dropped — the
// ImportResultsView only consumes missing + suggested.
type ResolvedTemplateResult = Pick<
  WorkoutImportValidationResult,
  | 'name'
  | 'template'
  | 'missingExerciseMapping'
  | 'suggestedExerciseMapping'
  | 'missingMetricMapping'
  | 'suggestedMetricMapping'
>;

// Shape of the /internal/workout-import-agent response body. The endpoint
// returns only the first stage-3 resolved template (plus timing) by default —
// the stage 1/2 intermediates and the remaining days are megabytes of unused
// JSON for multi-day PDFs, gated behind a `?fullStages=true` debug query param
// server-side.
type WorkoutImportAgentResponse = {
  ok?: boolean;
  error?: string;
  resolved?: ResolvedTemplateResult[];
};

// Run a PDF through the real stages 1-3 pipeline via the internal agent
// endpoint (the same one the AiPage test harness uses). Returns the first
// resolved template — a multi-day PDF produces multiple resolved templates,
// but the import UI shows one at a time, so we take the first day.
export async function processPdfViaAgent(
  pdfBase64: string,
  orgId: string,
): Promise<WorkoutImportValidationResult> {
  let response: Response;
  try {
    response = await fetch(
      `${environment.VITE_API_URL}/internal/workout-import-agent`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId, pdfBase64 }),
      },
    );
  } catch (error) {
    // An edge 504 (gateway timeout) is CORS-blocked by the browser, and an
    // offline browser rejects the same way: fetch throws a TypeError without
    // surfacing the response status (SC-5678). The rejection alone cannot
    // identify which one happened, so the message covers both. Distinguish
    // network-level failures from HTTP error responses: log the real cause
    // and surface an actionable message instead of the generic failure toast.
    // The Error goes through the API's error argument so its message and
    // stack survive; log context would sanitize them away.
    const cause = error instanceof Error ? error : new Error(String(error));
    datadogLogs.logger.error(
      'Workout import request failed at the network level',
      {},
      cause,
    );
    datadogRum.addError(cause, { source: 'workout-import' });
    throw new Error(
      'The import could not be completed — the connection failed or the server timed out. Please try again or use a smaller file.',
    );
  }

  let body: WorkoutImportAgentResponse | null = null;
  try {
    body = await response.json();
  } catch {
    // 401 sends a plain-text body, not JSON
  }

  const resolved = body?.ok ? body.resolved?.[0] : undefined;
  if (resolved) {
    return {
      name: resolved.name,
      template: resolved.template,
      missingExerciseMapping: resolved.missingExerciseMapping,
      suggestedExerciseMapping: resolved.suggestedExerciseMapping,
      missingMetricMapping: resolved.missingMetricMapping,
      suggestedMetricMapping: resolved.suggestedMetricMapping,
    };
  }

  if (typeof body?.error === 'string') {
    throw new Error(body.error);
  }
  throw new Error(
    response.status === 401 ? 'Not authorized' : 'Workout import failed',
  );
}

export function useWorkoutImport() {
  const { toast } = useToast();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationResult, setValidationResult] =
    useState<WorkoutImportValidationResult | null>(null);
  // Admin-editable template name. Seeded from the pipeline result's parsed
  // name; the admin can rename before saving (per SC-4420 spec). Empty name
  // disables Save.
  const [templateName, setTemplateName] = useState<string>('');
  // The working template layers the admin edits in the embedded
  // WorkoutTemplateSheet. Seeded from the pipeline's `template` with the
  // AI-suggested matches pre-applied (temp UUIDs → suggestedIds); updated via
  // onLayersChange as the admin edits/swaps cells before saving.
  const [template, setTemplate] = useState<WorkoutLayer[]>([]);
  // Monotonic counter bumped on each successful upload — used as a stable
  // React `key` for the editor so a new upload always remounts the sheet (which
  // keeps an internal local copy in editable mode), even if two uploads parse
  // to the same template name.
  const [uploadId, setUploadId] = useState(0);
  // Review state for suggested metrics. Each suggested metric (a temp UUID key
  // present in `suggestedMetricMapping`) starts "pending" — neither accepted nor
  // rejected — so the admin is prompted to review it before saving. Save stays
  // disabled while any suggestion is still pending (per spec). The suggestion is
  // auto-applied into the editor up front; "Approve" just clears the pending flag
  // (the metric stays), "Remove" strips the metric from the template + clears the
  // flag. A rejected suggestion does NOT re-block save — the metric is simply gone
  // from the saved template. Sets are keyed by the temp UUID (the suggestion key),
  // so they're stable across remounts.
  const [acceptedMetricIds, setAcceptedMetricIds] = useState<Set<string>>(
    new Set(),
  );
  const [rejectedMetricIds, setRejectedMetricIds] = useState<Set<string>>(
    new Set(),
  );
  // Review state for missing exercises that have no suggested match. Each starts
  // "pending" and must be Added (created in the org now, template repointed at
  // the new real id) or Removed (stripped from the template) before Save
  // enables. Keyed by the temp UUID from missingExerciseMapping.
  const [handledExerciseIds, setHandledExerciseIds] = useState<Set<string>>(
    new Set(),
  );
  const [addingExerciseIds, setAddingExerciseIds] = useState<Set<string>>(
    new Set(),
  );
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const orgsQuery = trpc.admin.orgs.useQuery(undefined);
  const saveTemplateMutation =
    trpc.admin.workoutImport.saveTemplate.useMutation();
  const createExerciseMutation =
    trpc.admin.workoutImport.createExerciseForOrg.useMutation();

  // Pending suggested metrics = suggestions the admin hasn't yet Approved or
  // Removed. Save is blocked while any remain (per spec: each must be reviewed
  // before saving). Derived from the validation result + the review sets.
  const pendingSuggestedMetricIds = useMemo(
    () =>
      validationResult
        ? Object.keys(validationResult.suggestedMetricMapping).filter(
            (id) => !acceptedMetricIds.has(id) && !rejectedMetricIds.has(id),
          )
        : [],
    [validationResult, acceptedMetricIds, rejectedMetricIds],
  );

  // Missing exercises with no suggested match, still awaiting Add/Remove. These
  // hold temp-UUID libraryExerciseId refs, so they must be resolved (created, or
  // dropped) before save — otherwise a temp UUID would reach saveTemplate.
  const pendingMissingExerciseIds = useMemo(
    () =>
      validationResult
        ? Object.keys(validationResult.missingExerciseMapping).filter(
            (id) =>
              !(id in validationResult.suggestedExerciseMapping) &&
              !handledExerciseIds.has(id),
          )
        : [],
    [validationResult, handledExerciseIds],
  );

  // Save is blocked when:
  //  - there are unresolved missing metrics (a missing metric with NO
  //    suggestion — those must be created externally before save), OR
  //  - there are pending suggested metrics awaiting review, OR
  //  - there are missing exercises still awaiting Add/Remove, OR
  //  - the editable name is empty/whitespace, OR
  //  - a request is in flight / no org selected.
  const canSave =
    !!validationResult &&
    !hasUnresolvedMissingMetrics(validationResult) &&
    pendingSuggestedMetricIds.length === 0 &&
    pendingMissingExerciseIds.length === 0 &&
    !isSaving &&
    !!selectedOrgId &&
    templateName.trim().length > 0;

  const resetResults = useCallback(() => {
    setFile(null);
    setValidationResult(null);
    setTemplateName('');
    setTemplate([]);
    setAcceptedMetricIds(new Set());
    setRejectedMetricIds(new Set());
    setHandledExerciseIds(new Set());
    setAddingExerciseIds(new Set());
  }, []);

  const handleTemplateNameChange = useCallback((name: string) => {
    setTemplateName(name);
  }, []);

  const handleLayersChange = useCallback((layers: WorkoutLayer[]) => {
    setTemplate(layers);
  }, []);

  // Accept a suggested metric: keep it in the template, mark reviewed. The
  // suggestion was already applied into the editor up front, so nothing about
  // the template changes — this just clears the pending flag so Save can enable.
  const handleApproveSuggestedMetric = useCallback((tempUuid: string) => {
    setAcceptedMetricIds((prev) => {
      const next = new Set(prev);
      next.add(tempUuid);
      return next;
    });
    // A metric can't be both accepted and rejected — clear the opposite flag.
    setRejectedMetricIds((prev) => {
      if (!prev.has(tempUuid)) return prev;
      const next = new Set(prev);
      next.delete(tempUuid);
      return next;
    });
  }, []);

  // Reject a suggested metric: strip it from the working template so no
  // lingering id reaches saveTemplate, mark reviewed, and remount the editor
  // (the sheet keeps an internal local copy in editable mode that ignores prop
  // updates, so it would still show the removed metric + clobber the strip on
  // the next cell edit without a remount). Removing does NOT re-block save —
  // the metric is simply absent from the saved template.
  const handleRemoveSuggestedMetric = useCallback(
    (tempUuid: string) => {
      if (!validationResult) return;
      const suggested = validationResult.suggestedMetricMapping[tempUuid];
      if (!suggested) return;
      setTemplate((prev) =>
        removeMetricFromLayers(prev, suggested.suggestedId),
      );
      setRejectedMetricIds((prev) => {
        const next = new Set(prev);
        next.add(tempUuid);
        return next;
      });
      setAcceptedMetricIds((prev) => {
        if (!prev.has(tempUuid)) return prev;
        const next = new Set(prev);
        next.delete(tempUuid);
        return next;
      });
      // Remount the editor so it re-initializes from the stripped template.
      setUploadId((id) => id + 1);
    },
    [validationResult],
  );

  // "Add" a missing exercise: actually create it in the target org now, then
  // repoint the template's temp-UUID ref at the new real id. Creating up front
  // (rather than deferring to saveTemplate) keeps the saved template free of
  // temp UUIDs — nothing to resolve server-side, no FK risk.
  const handleAddMissingExercise = useCallback(
    async (tempUuid: string) => {
      if (!selectedOrgId || !validationResult) return;
      const missing = validationResult.missingExerciseMapping[tempUuid];
      if (!missing) return;

      setAddingExerciseIds((prev) => new Set(prev).add(tempUuid));
      try {
        const created = await createExerciseMutation.mutateAsync({
          orgId: selectedOrgId,
          name: missing.name,
        });
        setTemplate((prev) => substituteExerciseId(prev, tempUuid, created.id));
        setHandledExerciseIds((prev) => new Set(prev).add(tempUuid));
        // Remount the editor so it re-initializes from the updated template.
        setUploadId((id) => id + 1);
        toast({
          title: 'Exercise created',
          description: `"${created.name}" was added to the organization's library`,
          severity: 'success',
        });
      } catch (error) {
        toast({
          title: 'Could not create exercise',
          description:
            error instanceof Error
              ? error.message
              : 'Failed to create the exercise. Please try again.',
          severity: 'error',
        });
      } finally {
        setAddingExerciseIds((prev) => {
          const next = new Set(prev);
          next.delete(tempUuid);
          return next;
        });
      }
    },
    [selectedOrgId, validationResult, createExerciseMutation, toast],
  );

  // "Remove" a missing exercise: strip it (and its sets/metrics) from the
  // working template so nothing referencing the temp UUID reaches saveTemplate.
  const handleRemoveMissingExercise = useCallback((tempUuid: string) => {
    setTemplate((prev) => removeExerciseFromLayers(prev, tempUuid));
    setHandledExerciseIds((prev) => new Set(prev).add(tempUuid));
    // Remount the editor so it re-initializes from the stripped template.
    setUploadId((id) => id + 1);
  }, []);

  const handleOrgChange = useCallback(
    (orgId: string | null) => {
      setSelectedOrgId(orgId);
      // Org changed after results were shown → reset to step 1 (per spec).
      resetResults();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [resetResults],
  );

  const processUploadedFile = useCallback(
    async (content: string, _fileName: string) => {
      if (!selectedOrgId) return;

      setIsProcessing(true);
      try {
        // PDFs run the real stages 1-3 pipeline (extract → build → resolve)
        // via the internal agent endpoint. CSV upload lands in a later phase.
        const result = await processPdfViaAgent(content, selectedOrgId);
        setValidationResult(result);
        // Reset review state for the new upload. The temp-UUID keys in
        // missing/suggested mappings may be reused across uploads (e.g. a
        // stable per-entity temp key). If a previous upload's Approve/Remove/
        // Add choices were kept, a temp UUID from the new upload could be
        // considered "handled" and reach saveTemplate, and the review banners
        // would silently skip entities that need a fresh decision.
        setAcceptedMetricIds(new Set());
        setRejectedMetricIds(new Set());
        setHandledExerciseIds(new Set());
        setAddingExerciseIds(new Set());
        // Seed the editable name from the parsed template name.
        setTemplateName(result.name);
        // Seed the working template with AI-suggested matches pre-applied so the
        // editor renders filled-in (suggested exercises/metrics resolved to their
        // existing org entity ids). The admin can then edit/swap any cell.
        // Unresolved metric refs (temp UUIDs with no accepted suggestion) are
        // stripped first — WorkoutTemplateSheet's getMetric throws for ids not in
        // the org, so they'd crash the editor. The error banner surfaces them
        // instead, and Save stays disabled while any remain.
        const withSuggestions = applySuggestedMappings(
          result.template,
          result.suggestedExerciseMapping,
          result.suggestedMetricMapping,
        );
        setTemplate(stripUnresolvedMetricRefs(withSuggestions, result));
        setUploadId((id) => id + 1);
      } catch (error) {
        toast({
          title: 'Error processing file',
          description:
            error instanceof Error
              ? error.message
              : 'Failed to process the file. Please try again.',
          severity: 'error',
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [selectedOrgId, toast],
  );

  const handleFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (!selectedFile) return;

      // accept=".pdf" matches case-insensitively in browsers — lowercase
      // before the extension check or "Workout.PDF" is rejected as invalid.
      const isPdf = selectedFile.name.toLowerCase().endsWith('.pdf');
      if (!isPdf) {
        toast({
          title: 'Invalid file type',
          description: 'Please select a PDF file',
          severity: 'error',
        });
        return;
      }

      setFile(selectedFile);
      setValidationResult(null);
      // Drop any review state from a previous upload so the next result can't
      // inherit stale Approve/Remove/Add decisions keyed on the same temp UUIDs.
      setAcceptedMetricIds(new Set());
      setRejectedMetricIds(new Set());
      setHandledExerciseIds(new Set());
      setAddingExerciseIds(new Set());

      try {
        // Both CSV and PDF are base64-encoded client-side and parsed
        // server-side by the import MCP — no client-side parsing.
        const content = await readFileAsBase64(selectedFile);
        await processUploadedFile(content, selectedFile.name);
      } catch (error) {
        toast({
          title: 'Error reading file',
          description:
            error instanceof Error
              ? error.message
              : 'Failed to read the file. Please try again.',
          severity: 'error',
        });
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [processUploadedFile, toast],
  );

  const handleSave = useCallback(async () => {
    if (!selectedOrgId || !validationResult || !canSave) return;

    setIsSaving(true);
    try {
      const result = await saveTemplateMutation.mutateAsync({
        orgId: selectedOrgId,
        name: templateName,
        template,
      });

      toast({
        title: 'Import successful',
        description: result.templateCreated
          ? `Template "${templateName}" saved to the organization's library`
          : 'Import complete',
        severity: 'success',
      });

      // Reset to step 1 after a successful save (per spec).
      resetResults();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast({
        title: 'Import failed',
        description:
          error instanceof Error
            ? error.message
            : 'An error occurred during import',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    selectedOrgId,
    validationResult,
    canSave,
    templateName,
    template,
    saveTemplateMutation,
    toast,
    resetResults,
  ]);

  return {
    selectedOrgId,
    file,
    fileInputRef,
    isProcessing,
    validationResult,
    template,
    uploadId,
    templateName,
    isSaving,
    canSave,
    orgsQuery,
    handleOrgChange,
    handleFileSelected,
    handleTemplateNameChange,
    handleLayersChange,
    handleApproveSuggestedMetric,
    handleRemoveSuggestedMetric,
    pendingSuggestedMetricIds,
    pendingMissingExerciseIds,
    addingExerciseIds,
    handleAddMissingExercise,
    handleRemoveMissingExercise,
    handleSave,
  };
}

export type UseWorkoutImportResult = ReturnType<typeof useWorkoutImport>;

// Re-export for the page so it doesn't reach into the exercise-library types.
export type { Org };
